import React from 'react';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import { ComponentsProps, ComponentsOverrides, ComponentsVariants, Theme } from '@mui/material/styles';
import { useLocaleText } from '@mui/x-date-pickers/internals/hooks/useUtils';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import { dayPickerClasses } from '@mui/x-date-pickers/DateCalendar/dayCalendarClasses';
import { pickersDayClasses } from '@mui/x-date-pickers/PickersDay/pickersDayClasses';

function CustomActionBar(props: PickersActionBarProps) {
    const { onAccept, onClear, onCancel, onSetToday, actions, ...other } = props;

    const localeText = useLocaleText();

    if (actions == null || actions.length === 0) {
        return null;
    }

    const buttons = actions?.map((actionType) => {
        switch (actionType) {
            case 'clear':
                return (
                    <Button data-mui-test="clear-action-button" onClick={onClear} key={actionType}>
                        {localeText.clearButtonLabel}
                    </Button>
                );

            case 'cancel':
                return (
                    <Button color="secondary" variant="outlined" onClick={onCancel} key={actionType}>
                        {localeText.cancelButtonLabel}
                    </Button>
                );

            case 'accept':
                return (
                    <Button onClick={onAccept} key={actionType}>
                        {localeText.okButtonLabel}
                    </Button>
                );

            case 'today':
                return (
                    <Button data-mui-test="today-action-button" onClick={onSetToday} key={actionType}>
                        {localeText.todayButtonLabel}
                    </Button>
                );

            default:
                return null;
        }
    });

    return <DialogActions {...other}>{buttons}</DialogActions>;
}

export const MuiDatePicker: {
    defaultProps?: ComponentsProps['MuiDatePicker'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiDatePicker'];
    variants?: ComponentsVariants['MuiDatePicker'];
} = {
    defaultProps: {
        slots: {
            actionBar: CustomActionBar,
        },
        slotProps: {
            desktopPaper: {
                sx: {
                    width: '22rem',
                },
            },
            mobilePaper: {
                sx: (theme) => ({
                    margin: theme.spacing(theme.tokens.spacing.none),
                    padding: theme.spacing(theme.tokens.spacing.sm),
                    width: '100%',
                    maxWidth: '100% !important',
                }),
            },
            dialog: {
                fullScreen: true,
                maxWidth: false,
                fullWidth: true,
            },
        },
    },
};

export const MuiDateCalendar: {
    defaultProps?: ComponentsProps['MuiDateCalendar'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiDateCalendar'];
    variants?: ComponentsVariants['MuiDateCalendar'];
} = {
    styleOverrides: {
        root: (props) => ({
            width: '100%',
            justifyContent: 'space-between',
            [`.${dayPickerClasses.header}`]: {
                justifyContent: 'space-between',
                padding: props.theme.spacing(props.theme.tokens.spacing.none, props.theme.tokens.spacing.sm),
            },
            [`.${dayPickerClasses.weekContainer}`]: {
                justifyContent: 'space-between',
                padding: props.theme.spacing(props.theme.tokens.spacing.none, props.theme.tokens.spacing.sm),
            },
        }),
    },
};

export const MuiYearCalendar: {
    defaultProps?: ComponentsProps['MuiYearCalendar'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiYearCalendar'];
    variants?: ComponentsVariants['MuiYearCalendar'];
} = {
    styleOverrides: {
        root: {
            width: '100%',
        },
    },
};

export const MuiPickersDay: {
    defaultProps?: ComponentsProps['MuiPickersDay'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiPickersDay'];
    variants?: ComponentsVariants['MuiPickersDay'];
} = {
    styleOverrides: {
        root: (props) => ({
            width: '2.25rem',
            height: '2.25rem',
            '&:hover': {
                backgroundColor: props.theme.palette.background.hover,
            },
            '&:focus': {
                backgroundColor: props.theme.palette.background.paper,
            },
            [`.${pickersDayClasses.selected}`]: {
                backgroundColor: props.theme.palette.primary.main,
                '&:hover': {
                    backgroundColor: props.theme.palette.primary.dark,
                },
                '&:focus': {
                    backgroundColor: props.theme.palette.primary.dark,
                },
            },
        }),
    },
};

export const MuiPickersYear: {
    defaultProps?: ComponentsProps['MuiPickersYear'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiPickersYear'];
    variants?: ComponentsVariants['MuiPickersYear'];
} = {
    styleOverrides: {
        yearButton: (props) => ({
            height: '2.25rem',
            width: '4.5rem',
            borderRadius: props.theme.borderRadius(props.theme.tokens.borderRadius.round),
        }),
    },
};
